/** ****************************************************************************
 * Main APP component for the iFrame.
 *
 * The iFrame (sub-project within the bPerx iFrame monorepo)'s main APP component.
 * Contains the displayoffer page, as well as terms, redeem, and not found.
 * Primary purpose of this sub-project is to display the offers in an iframe.
 * Partner websites will load this iFrame via bperx.js.
 *
 * @file   Main APP Component for sub-project "iFrame"
 * @author Paul J Stales, Steve Yardumian
 * @since  2.0.0
 **************************************************************************** */

import { FunctionalComponent, h } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { Router } from 'preact-router'
import transform from 'css-to-react-native-transform'
import { StyleSheet } from 'aphrodite'
import { IFRAME_CLASSNAMES } from '../../../../common/constants/ConstantsIFrameClassNames'
import { TransactionInformation } from '../../../../common/types/transactionInformationTypes/TransactionInformation'
import { PartnerSuppliedInfo } from '../../../../common/types/partnerSuppliedInfoTypes/PartnerSuppliedInfo'
import {
  updateTransactionInformationViaPostMessage,
  requestTransactionInformationFromLocalStorage,
  requestPartnerInfoFromParent,
  handleNonTypedMessage,
  handleMessageResponse
} from '../services/iframePostMessageService'

import DisplayOffer from '../routes/displayoffer'
import RedeemOffer from '../routes/redeemoffer'
import NotFoundPage from '../routes/notfound'
import { IFramePostMessageType } from '../../../../common/types/IFramePostMessage'
import { D, ERROR_MESSAGES } from '../../../../common/constants/ConstantsLogs'
import { _devLog } from '../../../../common/utilities/DevLoggingUtility'

// CONSTANTS
const IFRAME_BODY_ID = IFRAME_CLASSNAMES.__________BPERX_IFRAME__________
const DUMMY_TI: TransactionInformation = {
  customerChoice: {
    productMagazineChosen: {},
    isOptingIn: false,
    didViewProducts: false
  },
  voucherInformation: {}
}
const DUMMY_PI: PartnerSuppliedInfo = {
  frameInformation: {
    partnerId: 'PLACEHOLDER',
    offerId: '',
    placementName: '',
    querySelector: '',
    templateChoice: 'default',
    partnerApiKey: ''
  },
  partnerCustomizations: {}
}

/**
 *
 * Data-Driven State
 * @STATE: TransactionInformation: the object sent from the partner to this iframe via postMessage,
 *         containing information that affects the transaction (frame information, customer's
 *         choices, voucher, etc.)
 * @STATE: partnerSuppliedInfo: the object sent from the partner to this iframe via postMessage,
 *         containing information that affects the display (frame information, partner customizations)
 *
 * Flags
 * @STATE: isTransactionInformationAvailable: when this iFrame loads, it makes a request to the
 *         parent/partner webpage for the TransactionInformation, when the TI arrives this flag
 *         is set, so we can safely load data from the bPerx server (since the TI has the needed
 *         partner key to make the API call)
 * @STATE: doesPartnerNeedUpdateForTransactionInformation: useEffect cannot update the partner
 *         every time TransactionInformation updates; it would cause an infinite loop when the
 *         partner sends the TI for the first time. This flag will be set when a meaningful
 *         change is made, and then the partner will be updated via PostMessage. This allows
 *         for manual control over when to send the information to the partner, but keeps
 *         the actual function call is a single place.
 *
 * @returns
 */

const App: FunctionalComponent = () => {
  // ========== State =============================================================================

  // Data-Driven State
  const [transactionInformation, setTransactionInformation] =
    useState<TransactionInformation>(DUMMY_TI)
  const [partnerSuppliedInfo, setPartnerSuppliedInfo] =
    useState<PartnerSuppliedInfo>(DUMMY_PI)

  // Flags
  const [
    isTransactionInformationAvailable,
    setTransactionInformationAvailable
  ] = useState(false)
  const [isPartnerInfoAvailable, setIsPartnerInfoAvailable] = useState(false)
  const [
    doesPartnerNeedUpdateForTransactionInformation,
    setDoesPartnerNeedUpdateForTransactionInformation
  ] = useState(false)

  // ========== Use Effect Hooks ==================================================================

  // ----- [UE1] The partner needs the latest changes to TransactionInformation
  useEffect(() => {
    if (doesPartnerNeedUpdateForTransactionInformation) {
      updateTransactionInformationViaPostMessage(
        transactionInformation,
        partnerSuppliedInfo.frameInformation.partnerId
      )
      setDoesPartnerNeedUpdateForTransactionInformation(false)
    }
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doesPartnerNeedUpdateForTransactionInformation])

  // ----- [UE2] On initial load, request "TransactionInformation" and "PartnerInfo"
  useEffect(() => {
    requestPartnerInfoFromParent(partnerSuppliedInfo.frameInformation.partnerId)
    requestTransactionInformationFromLocalStorage(
      partnerSuppliedInfo.frameInformation.partnerId
    )
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ========== Event Listeners for Window ========================================================

  addEventListener('message', (messageEvent: MessageEvent) => {
    _devLog(
      D.IFRAME_ONMESSAGE_TRIGGERED,
      partnerSuppliedInfo.frameInformation.partnerId,
      undefined
    )

    // eslint-disable-next-line no-prototype-builtins
    if (!messageEvent.data.hasOwnProperty('messageType')) {
      handleNonTypedMessage(
        messageEvent,
        partnerSuppliedInfo.frameInformation.partnerId
      )
      return
    }

    switch (messageEvent.data.messageType) {
      case IFramePostMessageType.TRANSACTION_INFORMATION_RESPONSE:
        handleMessageResponse(
          messageEvent,
          partnerSuppliedInfo.frameInformation.partnerId,
          setTransactionInformationAvailable,
          setTransactionInformation
        )
        break

      case IFramePostMessageType.PARTNER_INFO_RESPONSE:
        handleMessageResponse(
          messageEvent,
          partnerSuppliedInfo.frameInformation.partnerId,
          setIsPartnerInfoAvailable,
          setPartnerSuppliedInfo
        )
        break

      default:
        console.error(ERROR_MESSAGES.ERROR_5002_MESSAGE_TYPE_NOT_RECOGNIZED)
        break
    }
  })

  if (!isPartnerInfoAvailable || !isTransactionInformationAvailable) {
    return <div id={IFRAME_BODY_ID} />
  }

  /**
   * This is how custom css is applied. It makes a styles object.
   */
  const styles = StyleSheet.create(
    // TODO: validate var instead of using ||
    transform(
      partnerSuppliedInfo.partnerCustomizations?.customCss?.content || ''
    ) as {
      [key: string]: any
    }
  )

  // set a font URL for import, if available
  if (partnerSuppliedInfo.partnerCustomizations?.customCss?.fontUrl) {
    const linkElement = document.createElement('link')
    linkElement.rel = 'stylesheet'
    linkElement.href =
      partnerSuppliedInfo.partnerCustomizations?.customCss?.fontUrl
    document.head.appendChild(linkElement)
  }

  return (
    <div id={IFRAME_BODY_ID}>
      <Router>
        <DisplayOffer
          path="/displayoffer"
          styles={styles}
          transactionInformation={transactionInformation}
          isTransactionInformationAvailable={isTransactionInformationAvailable}
          setTransactionInformation={setTransactionInformation}
          setDoesPartnerNeedUpdateForTransactionInformation={
            setDoesPartnerNeedUpdateForTransactionInformation
          }
          isPartnerInfoAvailable={isPartnerInfoAvailable}
          partnerSuppliedInfo={partnerSuppliedInfo}
        />
        <RedeemOffer
          path="/redeemoffer"
          styles={styles}
          transactionInformation={transactionInformation}
          isTransactionInformationAvailable={isTransactionInformationAvailable}
          setTransactionInformation={setTransactionInformation}
          setDoesPartnerNeedUpdateForTransactionInformation={
            setDoesPartnerNeedUpdateForTransactionInformation
          }
          partnerSuppliedInfo={partnerSuppliedInfo}
          isPartnerInfoAvailable={isPartnerInfoAvailable}
        />
        <NotFoundPage default={true} styles={styles} />
      </Router>
    </div>
  )
}

export default App
